/**
 * Shared chrome for the Email Marketing section: a horizontal sub-nav +
 * feature-flag gate. Each email page wraps its body in <EmailSectionLayout>.
 */
import { NavLink } from 'react-router-dom';
import { useFeature, FeatureLockedCard } from '../hooks/useFeatures';
import { Mail, Users, ListChecks, Send, Plug, Loader2 } from 'lucide-react';

const TABS = [
  { to: '/email', label: 'Overview', icon: Mail, end: true },
  { to: '/email/campaigns', label: 'Campaigns', icon: Send },
  { to: '/email/subscribers', label: 'Subscribers', icon: Users },
  { to: '/email/lists', label: 'Lists', icon: ListChecks },
  { to: '/email/providers', label: 'Provider', icon: Plug },
];

export const EmailSectionLayout = ({ children, actions = null }) => {
  const { loading, locked, requiredTiers } = useFeature('email_marketing');

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="max-w-6xl mx-auto p-6" data-testid="email-marketing-page">
        <FeatureLockedCard
          featureName="Email Marketing"
          requiredTiers={requiredTiers}
          description="Build subscriber lists and send broadcasts & sequences through your own SendGrid account."
          testId="email-feature-locked"
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="email-marketing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 leading-tight">Email Marketing</h1>
            <p className="text-sm text-zinc-500">Send personal, plain-text email through your own SendGrid account.</p>
          </div>
        </div>
        {actions}
      </div>

      <div className="border-b border-zinc-200 -mx-1">
        <nav className="flex gap-1 overflow-x-auto" data-testid="email-subnav">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`email-tab-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-zinc-500 hover:text-zinc-800'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {children}
    </div>
  );
};
